import { useEffect, useRef, useState } from 'react'
import Modal from './Modal'
import { api, ApiClientError } from '@/lib/api'
import { toastError } from '@/store/toast'
import type { Customer, Patient } from '@yui-receipt/shared'

type Props = {
  open: boolean
  onClose: () => void
  onSelect: (c: Customer) => void
  // Optional callback for "edit" action — parent opens ManualCustomerModal
  // in edit mode. If omitted, the edit button is hidden.
  onEdit?: (c: Customer) => void
}

type SearchResult = {
  type: 'customer'
  customer: Customer
} | {
  type: 'patient'
  patient: Patient
}

export default function CustomerSearchModal({ open, onClose, onSelect, onEdit }: Props) {
  const [q, setQ] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (open) {
      setQ('')
      setResults([])
      setTimeout(() => inputRef.current?.focus(), 50)
    }
  }, [open])

  // Debounced search: searches both customers and patients, deduplicates
  useEffect(() => {
    if (!open) return
    const query = q.trim()
    const controller = new AbortController()
    const t = setTimeout(async () => {
      setLoading(true)
      try {
        // Search both tables in parallel
        const [custRes, patRes] = await Promise.all([
          api<{ customers: Customer[]; total: number }>(
            query ? `/customers?q=${encodeURIComponent(query)}&limit=20` : '/customers?limit=20',
            { signal: controller.signal },
          ),
          api<{ patients: Patient[] }>(
            query ? `/patients?q=${encodeURIComponent(query)}&limit=20` : '/patients?limit=20',
            { signal: controller.signal },
          ),
        ])

        const merged: SearchResult[] = []
        const seenCustomerIds = new Set<number>()

        // Add customers first
        for (const c of custRes.customers ?? []) {
          seenCustomerIds.add(c.id)
          merged.push({ type: 'customer', customer: c })
        }

        // Add patients that aren't already represented by a customer
        for (const p of patRes.patients ?? []) {
          if (p.customerId && seenCustomerIds.has(p.customerId)) continue
          merged.push({ type: 'patient', patient: p })
        }

        setResults(merged)
      } catch (err) {
        if (controller.signal.aborted) return
        const msg = err instanceof ApiClientError ? err.message : 'ค้นหาไม่สำเร็จ'
        toastError(msg)
      } finally {
        setLoading(false)
      }
    }, 300)
    return () => {
      clearTimeout(t)
      controller.abort()
    }
  }, [q, open])

  function pickCustomer(c: Customer) {
    onSelect(c)
    onClose()
  }

  async function pickPatient(p: Patient) {
    // Patient may have a linked customer — fetch it
    if (p.customerId) {
      try {
        const res = await api<{ customer: Customer }>(`/customers/${p.customerId}`)
        onSelect(res.customer)
        onClose()
        return
      } catch {
        // fallback: create a synthetic customer-like object
      }
    }
    // No linked customer — create a minimal Customer-like object for POS
    // The receipt can be created without a customerId, but we pass the patient name
    const synthetic: Customer = {
      id: 0,
      cidMasked: '',
      cidLast4: '',
      cidFull: null,
      nameTh: p.nameTh,
      nameEn: p.nameEn,
      dob: p.dob,
      gender: p.sex,
      address: null,
      phone: p.phone,
      createdAt: p.createdAt,
    }
    onSelect(synthetic)
    onClose()
  }

  return (
    <Modal open={open} onClose={onClose} title="🔍 ค้นหาคนไข้" widthClass="max-w-2xl">
      <div className="space-y-3">
        <div>
          <input
            ref={inputRef}
            className="input text-base"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="พิมพ์ชื่อ / เบอร์โทร / HN / CID 4 ตัวท้าย"
          />
          <p className="text-xs text-slate-500 mt-1">
            ค้นจากชื่อไทย / ชื่ออังกฤษ / เบอร์โทร / HN / 4 ตัวท้ายบัตรประชาชน
          </p>
        </div>

        <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-[50vh] overflow-auto">
          {loading ? (
            <div className="p-8 text-center text-slate-500">กำลังค้นหา...</div>
          ) : results.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              {q.trim() ? 'ไม่พบคนไข้' : 'ยังไม่มีคนไข้ในระบบ'}
            </div>
          ) : (
            results.map((r) => {
              if (r.type === 'customer') {
                const c = r.customer
                return (
                  <div
                    key={`c-${c.id}`}
                    className="p-3 flex items-center gap-3 hover:bg-slate-50"
                  >
                    <button
                      className="flex-1 text-left"
                      onClick={() => pickCustomer(c)}
                    >
                      <div className="font-medium">{c.nameTh}</div>
                      <div className="text-xs text-slate-500 font-mono">
                        {c.cidFull ?? c.cidMasked}
                        {c.phone && <span className="ml-2">· {c.phone}</span>}
                      </div>
                      {c.nameEn && <div className="text-xs text-slate-400">{c.nameEn}</div>}
                    </button>
                    <button
                      className="btn-primary text-sm"
                      onClick={() => pickCustomer(c)}
                      title="เลือกคนไข้"
                    >
                      ใช้
                    </button>
                    {onEdit && (
                      <button
                        className="btn-ghost text-sm border border-slate-200"
                        onClick={() => onEdit(c)}
                        title="แก้ไข"
                      >
                        ✏️
                      </button>
                    )}
                  </div>
                )
              } else {
                const p = r.patient
                return (
                  <div
                    key={`p-${p.id}`}
                    className="p-3 flex items-center gap-3 hover:bg-slate-50"
                  >
                    <button
                      className="flex-1 text-left"
                      onClick={() => pickPatient(p)}
                    >
                      <div className="font-medium">{p.nameTh}</div>
                      <div className="text-xs text-slate-500 font-mono">
                        HN {p.hn}
                        {p.phone && <span className="ml-2">· {p.phone}</span>}
                      </div>
                      {p.nameEn && <div className="text-xs text-slate-400">{p.nameEn}</div>}
                    </button>
                    <button
                      className="btn-primary text-sm"
                      onClick={() => pickPatient(p)}
                      title="เลือกคนไข้"
                    >
                      ใช้
                    </button>
                  </div>
                )
              }
            })
          )}
        </div>
      </div>
    </Modal>
  )
}
